import React from "react";
import { Field, Formik, FormikHelpers } from "formik";
import { Input } from "../../fields/Input";
import appService from "../../../services/app.service";
import { Loading } from "../../Loading";
import * as Yup from "yup";

import { useHistory } from "react-router-dom";
import { IConsultant, IConsultantDepartment, IConsultantFunction, IConsultantLocation } from "../../../core/cm.types";
import { LocationAwareButton } from "../../LocationAwareButton";
import { useTranslation } from "react-i18next";
import { Select } from "../../fields/Select";
import { NoAdminAlert } from "../../NoAdminAlert"
import MaskedInput from "react-text-mask";
import { PhoneField } from "../../fields/PhoneField";

const validationSchema = Yup.object().shape({
    name: Yup.string().required("*"),
    lastName: Yup.string().required("*"),
    email: Yup.string().email("Bitte eine gültige E-Mail Addresse angeben")
});

export const ConsultantDetails: React.FunctionComponent<{
    id: string | undefined;
    isAdmin: boolean;
}> = (props) => {
    const { t } = useTranslation();
    const { id, isAdmin } = props;
    const backButton = "/config/consultants"
    const isNew = id === undefined || id === null;
    const title = <h1 className="title is-3">{t("client:headers:tabConsultantDetail")}</h1>
    const history = useHistory();
    const [data, setData] = React.useState<{
        isLoading: boolean;
        data: IConsultant;
    }>({
        isLoading: true,
        data: {},
    });
    const [consultantFunctions, setConsultantFunctions] = React.useState<IConsultantFunction[]>([]);
    const [consultantLocations, setConsultantLocations] = React.useState<IConsultantLocation[]>([]);
    const [departments, setDepartmentOptions] = React.useState<IConsultantDepartment[]>([]);

    React.useEffect(() => {
        const fetchData = async () => {
            const data = await appService.getConsultant(id);
            setData({
                isLoading: false,
                data: data,
            });
        };

        appService.getConsultantFunctions().then((functions) => {
            setConsultantFunctions(functions);
        })

        appService.getConsultantDepartments().then((functions) => {
            setDepartmentOptions(functions);
        })

        appService.getConsultantLocations().then((functions) => {
            setConsultantLocations(functions);
        })

        if (isNew) {
            setData({
                isLoading: false,
                data: {},
            });
        } else {
            fetchData();
        }
    }, []);

    const functionOption = [{ key: "", text: t("client:tabConsultantDetail:selectFunction") }, ...consultantFunctions?.map((item) => {
        return {
            key: item.key,
            text: item.title
        };
    })];

    const officeLocationOption = [{ key: "", text: t("client:tabConsultantDetail:selectLocation") }, ...consultantLocations?.map((item) => {
        return {
            key: item.key,
            text: item.title
        };
    })];

    const departmentOptions = [{ key: "", text: t("client:tabConsultantDetail:selectDepartment") }, ...departments?.map((item) => {
        return {
            key: item.key,
            text: item.title
        };
    })];

    const onSubmit = (
        values: Readonly<IConsultant | undefined>,
        formikHelpers: FormikHelpers<Readonly<IConsultant>>
    ) => {
        const data = values;

        const submit = async () => {
            if (data !== undefined && data !== null) {
                appService.saveConsultant(data).then((modulesResults) => {
                    var location = appService.getLocation();
                    history.push(`/${location}${backButton}`)
                }).catch(() => {
                    alert(t("client:common:errorOnSave"));
                });
            }
        };

        submit();
    };

    const plzNumberMask = [
        /\d/,
        /\d/,
        /\d/,
        /\d/
    ];


    if (isAdmin !== true) { return <NoAdminAlert /> }

    return (
        <React.Fragment>
            <div className="section defaultSection">
                <Loading isLoading={data.isLoading}></Loading>
                {data.isLoading === false && (
                    <Formik
                        initialValues={data.data}
                        onSubmit={onSubmit}
                        validateOnMount={true}
                        validationSchema={validationSchema}
                        enableReinitialize
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            isSubmitting,
                            isValid,
                            dirty,
                            setFieldValue,
                        }) => (
                            <React.Fragment>
                                <form onSubmit={handleSubmit} className="form">
                                    <div className="columns">
                                        <div className="column">{title}</div>
                                        <div className="column button-header">
                                            <button
                                                type="submit"
                                                className={`button is-link is-small is-pulled-right backAndSaveButton ${isSubmitting ? t("client:common:isLoading") : ""
                                                    }`}
                                                disabled={isSubmitting || !dirty || !isValid}
                                            >
                                                {t("client:common:saveButton")}
                                            </button>
                                            <LocationAwareButton title={t("client:common:backButton")} className="button is-small is-pulled-right backAndSaveButton" relativeLocation={backButton}></LocationAwareButton>
                                        </div>
                                    </div>
                                    <Field
                                        name="name"
                                        label={t("client:config:columnFirstName")}
                                        component={Input}
                                    />
                                    <Field
                                        name="lastName"
                                        label={t("client:config:columnLastName")}
                                        component={Input}
                                    />
                                    <Field
                                        name="functionId"
                                        label={t("client:tabConsultantDetail:function")}
                                        options={functionOption}
                                        component={Select}
                                    />

                                    <Field
                                        name="departmentId"
                                        label={t("client:config:columnDepartment")}
                                        options={departmentOptions}
                                        component={Select}
                                    />
                                    <PhoneField name="phone" label={t("client:config:columnPhone")} placeholder={t("client:config:columnPhonePlaceholder")} />
                                    <Field
                                        name="email"
                                        label={t("client:config:columnEmail")}
                                        component={Input}
                                        suffixControl={
                                            <div className="control">
                                                <a className="button" href={`mailto:${values?.email}`}>
                                                    <i className={`fas fa-envelope`} /></a>
                                            </div>
                                        }
                                    />

                                    <Field
                                        name="locationId"
                                        label={t("client:tabConsDepLocation:locationName")}
                                        options={officeLocationOption}
                                        component={Select}
                                    />

                                    <div className="button-footer">
                                        <button
                                            type="submit"
                                            className={`button is-link is-small is-pulled-right backAndSaveButton${isSubmitting ? t("client:common:isLoading") : ""
                                                }`}
                                            disabled={isSubmitting || !dirty || !isValid}
                                        >
                                            {t("client:common:saveButton")}
                                        </button>
                                        <LocationAwareButton title={t("client:common:backButton")} className="button is-small is-pulled-right backAndSaveButton" relativeLocation={backButton}></LocationAwareButton>
                                    </div>
                                </form>
                            </React.Fragment>
                        )}
                    </Formik>
                )}
            </div>
        </React.Fragment>
    );
};
